import React from 'react';
import { Box, Paper, Typography, Button, LinearProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ProfileProgressBanner = ({ userProfile }) => {
    const navigate = useNavigate();

    if (!userProfile || Object.keys(userProfile).length === 0) return null;

    const hasData = (field) => {
        if (field === null || field === undefined) return false; 
        if (Array.isArray(field)) return field.length > 0; 
        if (typeof field === 'string') {
            const clean = field.trim();
            return clean !== '' && clean !== '{}' && clean !== '[]' && clean !== '""' && clean !== 'null';
        }
        if (typeof field === 'object') return Object.keys(field).length > 0; 
        return true;
    };

    // 1. Evaluate Personal
    const hasPersonal = (hasData(userProfile?.FirstNames) || hasData(userProfile?.FirstName)) && 
                        (hasData(userProfile?.Surname) || hasData(userProfile?.LastName));
    
    // 2. Evaluate Address
    const addr = userProfile?.Address || {};
    const hasAddress = (hasData(addr.Street) || hasData(userProfile?.StreetAddress) || hasData(userProfile?.Street)) && 
                       (hasData(addr.City) || hasData(userProfile?.City)) && 
                       (hasData(addr.Province) || hasData(userProfile?.Province));

    // 3. Evaluate Academic
    const hasAcademic = hasData(userProfile?.AcademicResults) || 
                        hasData(userProfile?.AcademicInfo) || 
                        hasData(userProfile?.academic);

    // Calculate gamified progress
    let completedSteps = 0;
    if (hasPersonal) completedSteps += 1;
    if (hasAddress) completedSteps += 1;
    if (hasAcademic) completedSteps += 1;
    
    const progressPercentage = Math.round((completedSteps / 3) * 100);
    const isFullyComplete = progressPercentage === 100;

    return (
        <Paper 
            // 🚨 THE FIX: Use the MUI theme callback to adapt colors based on light/dark mode
            sx={(theme) => ({ 
                p: 3, 
                mb: 4, 
                borderRadius: 3, 
                border: '1px solid',
                borderColor: isFullyComplete 
                    ? (theme.palette.mode === 'dark' ? 'success.dark' : 'success.light') 
                    : (theme.palette.mode === 'dark' ? 'warning.dark' : 'warning.light'),
                bgcolor: isFullyComplete 
                    ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : '#f2fdf4') 
                    : (theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.08)' : '#fffcf5')
            })}
            elevation={0}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isFullyComplete ? <CheckCircleIcon color="success" /> : <WarningAmberIcon color="warning" />}
                        Profile Completion: {progressPercentage}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isFullyComplete 
                            ? "Your profile is complete! Your bursary applications are ready for review."
                            : "Your profile is missing crucial information needed for your application to be reviewed."}
                    </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {!hasPersonal && (
                        <Button 
                            variant="contained" 
                            color="warning" 
                            onClick={() => navigate('/profile?section=personal')}
                            sx={{ fontWeight: 'bold', boxShadow: 'none' }}
                        >
                            ADD PERSONAL DETAILS
                        </Button>
                    )}

                    {!hasAddress && (
                        <Button 
                            variant="contained" 
                            color="warning" 
                            onClick={() => navigate('/profile?section=address')}
                            sx={{ fontWeight: 'bold', boxShadow: 'none' }}
                        >
                            ADD ADDRESS DETAILS
                        </Button>
                    )}

                    {!hasAcademic && (
                        <Button 
                            variant="contained" 
                            color="warning" 
                            onClick={() => navigate('/profile?section=academic')}
                            sx={{ fontWeight: 'bold', boxShadow: 'none' }}
                        >
                            ADD ACADEMIC RESULTS
                        </Button>
                    )}
                </Box>
            </Box>

            <LinearProgress 
                variant="determinate" 
                value={progressPercentage} 
                color={isFullyComplete ? "success" : "warning"}
                sx={{ height: 10, borderRadius: 5, mb: 2 }} 
            />

            {!hasPersonal && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    <strong>Action Required:</strong> You have missing Personal details. Please ensure your Name, Surname, and ID Number are filled in.
                </Alert>
            )}

            {!hasAddress && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    <strong>Action Required:</strong> You have missing Address details. Please ensure your Street Name, City, and Province are all filled in.
                </Alert>
            )}

            {!hasAcademic && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    <strong>Action Required:</strong> You have missing Academic Results. Applications without academic data risk immediate rejection. Please update your profile immediately.
                </Alert>
            )}
        </Paper>
    );
};

export default ProfileProgressBanner;